import React, { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import DevopsRecordShell from "../components/devops/DevopsRecordShell";
import MapAppsSection from "../components/devops/MapAppsSection";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import { DataSensitivity, EntityStatus } from "../types";
import type { DevopsApplication, DevopsEnvironment } from "../types/devops";
import {
  canMaintainEnvironment,
  canMapEnvironmentApplications,
  parseCsvList,
} from "../utils/devopsAccess";
import { devopsRoutes, type DevopsRecordMode } from "../utils/devopsRoutes";
import { getTenantId } from "../utils/jwtUtils";

interface EnvironmentRecordPageProps {
  mode: DevopsRecordMode;
}

const EnvironmentRecordPage: React.FC<EnvironmentRecordPageProps> = ({ mode }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bindEntity } = useDevopsWorkspace();
  const roles = user?.roles ?? [];
  const canWrite = canMaintainEnvironment(roles);
  const canMap = canMapEnvironmentApplications(roles);
  const readOnly = mode === "view";

  const [item, setItem] = useState<DevopsEnvironment | null>(null);
  const [apps, setApps] = useState<DevopsApplication[]>([]);
  const [loading, setLoading] = useState(mode !== "create");
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState(user?.email ?? "");
  const [managers, setManagers] = useState("");
  const [sharedConfig, setSharedConfig] = useState("");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.ACTIVE);
  const [dataSensitivity, setDataSensitivity] = useState<DataSensitivity>(
    DataSensitivity.INTERNAL,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "edit" && !canWrite && uuid) {
      navigate(devopsRoutes.environmentView(uuid), { replace: true });
    }
  }, [mode, canWrite, uuid, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (mode === "create") {
        const applications = await devopsService.listApplications().catch(() => []);
        if (!cancelled) setApps(applications);
        return;
      }
      if (!uuid) return;
      setLoading(true);
      setError(null);
      try {
        const [env, applications] = await Promise.all([
          devopsService.getEnvironment(uuid),
          devopsService.listApplications(),
        ]);
        if (cancelled) return;
        if (!env) {
          setError("Environment not found.");
          return;
        }
        setItem(env);
        setApps(applications);
        setName(env.name);
        setPurpose(env.purpose ?? "");
        setDescription(env.description ?? "");
        setOwner(env.owner);
        setManagers((env.managers ?? []).join(", "));
        setSharedConfig(env.sharedConfig ?? "");
        setStatus(env.status);
        setDataSensitivity(env.dataSensitivity ?? DataSensitivity.INTERNAL);
        bindEntity({ type: "ENVIRONMENT", uuid: env.uuid, name: env.name });
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load environment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [mode, uuid, bindEntity]);

  const handleSave = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setError("No tenant on the current session.");
      return;
    }
    if (!name.trim() || !owner.trim()) {
      setError("Name and owner are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const managersList = parseCsvList(managers);
      const payload = {
        name: name.trim(),
        purpose: purpose.trim() || undefined,
        description: description.trim() || undefined,
        owner: owner.trim(),
        managers: managersList,
        sharedConfig: sharedConfig.trim() || undefined,
        status,
        dataSensitivity,
      };
      const saved = item
        ? await devopsService.updateEnvironment(item.uuid, payload)
        : await devopsService.createEnvironment({ tenantId, ...payload });
      navigate(devopsRoutes.environmentView(saved.uuid));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save environment");
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "create" ? "Add environment" : mode === "edit" ? `Edit ${item?.name ?? "environment"}` : item?.name ?? "Environment";

  if (loading) return <CircularProgress size={24} />;

  return (
    <DevopsRecordShell
      title={title}
      backPath={mode === "edit" && uuid ? devopsRoutes.environmentView(uuid) : devopsRoutes.environments}
      backLabel={mode === "edit" ? "Back to view" : "Back to list"}
      actions={
        mode === "view" && canWrite && uuid ? (
          <Button variant="contained" onClick={() => navigate(devopsRoutes.environmentEdit(uuid))}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required disabled={readOnly} />
      <TextField label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} disabled={readOnly} />
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        minRows={2}
        disabled={readOnly}
      />
      <TextField label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} required disabled={readOnly} />
      <TextField
        label="Managers"
        value={managers}
        onChange={(e) => setManagers(e.target.value)}
        helperText="Comma-separated emails or user ids"
        disabled={readOnly}
      />
      <TextField
        select
        label="Data sensitivity"
        value={dataSensitivity}
        onChange={(e) => setDataSensitivity(e.target.value as DataSensitivity)}
        disabled={readOnly}
      >
        {Object.values(DataSensitivity).map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value as EntityStatus)}
        disabled={readOnly}
      >
        {Object.values(EntityStatus).map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Shared config"
        value={sharedConfig}
        onChange={(e) => setSharedConfig(e.target.value)}
        multiline
        minRows={6}
        helperText="Env-wide YAML or properties. Do not store secret values."
        disabled={readOnly}
      />
      {!readOnly && (
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ alignSelf: "flex-start" }}>
          Save
        </Button>
      )}
      {item && (
        <MapAppsSection
          environment={item}
          applications={apps}
          canEdit={canMap}
          onSaved={(saved) => {
            setItem(saved);
            bindEntity({ type: "ENVIRONMENT", uuid: saved.uuid, name: saved.name });
          }}
        />
      )}
    </DevopsRecordShell>
  );
};

export default EnvironmentRecordPage;
